import apiRequest from '@/services/api-request.js';
import clonedeep from 'lodash.clonedeep';
const state = {
	tags: [],
	tagGroups: [],
	assignments: {},
	activeTags: {},
	activeStepTags: {},
	activeAlternativeProductsTags: {},
	activeStep: null,
	storedTags: {},
	tagsPublicationId: ''
};
const getters = {
	publicationId: (state, getters, rootState) => {
		return rootState.hcube.publication_id;
	},
	publicationBaseUrl: (state, getters, rootState) => {
		return `${rootState.publicationEndpoint}/${getters.publicationId}`;
	},
	tagGroups: (state) => {
		return state.tagGroups;
	},
	tags: (state) => {
		return state.tags;
	},
	activeTags: (state) => {
		return state.activeTags;
	},
	activeWorkflowTags: (state) => {
		let activeGroupIds = Object.keys(state.activeTags);
		return activeGroupIds
			.filter((groupId) => {
				return state.tagGroups[groupId].scope === 'WORKFLOW';
			})
			.reduce((acc, groupId) => {
				return { ...acc, [groupId]: state.activeTags[groupId] };
			}, {});
	},

	activeStepTags: (state, getters) => {
		return state.activeStepTags;
	},
	activeAlternativeProductsTags: (state, getters) => {
		let currentStepTags = state.activeAlternativeProductsTags[state.activeStep];
		let activeGroupIds = currentStepTags ? Object.keys(currentStepTags) : [];
		return activeGroupIds.reduce((acc, groupId) => {
			return { ...acc, [groupId]: currentStepTags[groupId] };
		}, {});
	},
	productTagAssignments: (state) => (product) => {
		return state.assignments[product.key]?.map((tagId) => state.tags[tagId]) || [];
	},
	uniqueAssignedTags: (state, getters) => {
		let tagIds = [];
		if (state.assignments) {
			for (let productKey in state.assignments) {
				let tag = [...state.assignments[productKey]];
				if (tag.length) {
					tagIds = [...tagIds, ...tag];
				}
			}
		}
		let uniqueTagIds = new Set(tagIds);
		return Array.from(uniqueTagIds).map((id) => getters['tags'][id]);
	},
	uniqueAssignedTagGroups: (state, getters) => (scope) => {
		let assignedTags = getters['uniqueAssignedTags'].filter((t) => !!t['group-id']); //remove tags not assignet to any group
		let groupIds = [];
		for (let tag in assignedTags) {
			groupIds = [...groupIds, assignedTags[tag]['group-id']];
		}
		let uniqueGroupIds = new Set(groupIds);
		let uniqueTagGroups = Array.from(uniqueGroupIds).map((id) => getters['tagGroups'][id]);

		const tagGroupsHavePosition = uniqueTagGroups.every(tagGroup => tagGroup.hasOwnProperty('position'));
		const sortByName = (uniqueTagGroups) => uniqueTagGroups.sort((a, b) => a.name < b.name ? -1 : 1);
		const sortByPosition = (uniqueTagGroups) => uniqueTagGroups.sort((a, b) => a.position - b.position);

		const uniqueSortedTagGroups = tagGroupsHavePosition ? sortByPosition(uniqueTagGroups) : sortByName(uniqueTagGroups)

		return scope ? uniqueSortedTagGroups.filter((group) => group?.scope === scope) : uniqueSortedTagGroups;
	},
};
const actions = {
	setActiveStep: (context, { step, products }) => {
		context.commit('setActiveStep', step);

		if (!context.state.activeAlternativeProductsTags[step]) {
			context.commit('setActiveAlternativeProductsTags', { tags: [] });
		}
	},
	fetchTags: (context, { publicationId = null } = {}) => {
		const baseUrl = publicationId ? `${context.rootState.publicationEndpoint}/${publicationId}` : context.getters.publicationBaseUrl;

		return apiRequest(`${baseUrl}/tags`).then((r) => {
			context.state.tagsPublicationId = publicationId || context.getters.publicationId;
			context.commit('setTags', r);
		});
	},
	fetchTagAssignments: (context, { products, publicationId = null } = {}) => {
		const baseUrl = publicationId ? `${context.rootState.publicationEndpoint}/${publicationId}` : context.getters.publicationBaseUrl;
		return apiRequest(`${baseUrl}/tag-assignments`, 'get', null, null, {
			params: { products: products.join(',') },
		}).then((assignments) => {
			context.state.tagsPublicationId = publicationId || context.getters.publicationId;
			context.commit('setAssignments', assignments);
			//reset active tags and setDefaultTags only if this is not a stored/shared workflow
			const resetTags = !(context.rootState.isSavedWorkflow || context.rootState.isSharedWorkflow);
			if (resetTags) context.dispatch('resetActiveTags');
		});
	},
	resetActiveTags: (context, { setDefaultTags = true } = {}) => {
		context.commit('clearActiveTags');
		context.commit('clearActiveStepTags');
		context.commit('clearActiveAlternativeProductsTags');
		if (setDefaultTags) {
			let groups = context.getters['uniqueAssignedTagGroups']('WORKFLOW');
			const uniqueAssignedTags = context.getters['uniqueAssignedTags'];
			let activeWorflowTags = groups.reduce((acc, group) => {
				let existingTags = group.tags.reduce((tags, tagId) => {
					return [...tags, ...uniqueAssignedTags.filter((tag) => tag.id === tagId)];
				}, []);
				return existingTags?.length > 0 ? [...acc, { 'group-id': group.id, 'id': existingTags[0].id }] : acc;
			}, []);
			context.commit('setActiveTags', activeWorflowTags);
			context.dispatch('setDefaultActiveStepsTags');
		}
	},
	setActiveTag: (context, tag) => {
		context.commit('setActiveTags', [tag]);
		context.commit('clearActiveAlternativeProductsTags');
	},
	unsetActiveTag: (context, tag) => {
		context.commit('unsetActiveTags', [tag]);
		context.commit('clearActiveAlternativeProductsTags');
	},
	setActiveStepTag: (context, payload) => {
		context.commit('setActiveStepTags', [{ step: payload.step, tag: payload.tag }]);
	},
	unsetActiveStepTag: (context, payload) => {
		context.commit('unsetActiveStepTags', [{ step: payload.step, tag: payload.tag }]);
	},
	setActiveAlternativeProductsTag: (context, { tag }) => {
		context.commit('setActiveAlternativeProductsTags', { tags: [tag] });
	},
	unsetActiveAlternativeProductsTag: (context, { tag }) => {
		context.commit('unsetActiveAlternativeProductsTags', { tags: [tag] });
	},
	setDefaultActiveStepsTags: (context) => {
		const stepsProducts = context.rootGetters['workflow/stepsProducts'];
		const steps = Object.keys(stepsProducts);
		context.commit('clearActiveStepTags');
		const defaultStepsTags = steps.reduce((acc, step) => {
			const products = stepsProducts[step];
			const productsTagsIds = products
				.map((p) => context.getters['productTagAssignments'](p).map((tag) => tag.id))
				.flat();
			const tags = Array.from(new Set(productsTagsIds)).map((id) => context.getters['tags'][id]);
			let tagGroups = context.getters['uniqueAssignedTagGroups']('STEP');
			const displayed = tagGroups.reduce((acc, group) => {
				//filter groups with less than 2 tags in current workflow, sorted by order in group.tags
				let existingTags = group.tags.reduce((tagsAcc, tagId) => {
					return [...tagsAcc, ...tags.filter((tag) => tag.id === tagId)];
				}, []);
				return existingTags.length > 1 ? [...acc, { ...group, tags: existingTags }] : acc;
			}, []);
			if (displayed.length) {
				const defaultStepTags = displayed.map((group) => {
					return { step, tag: group.tags[0] };
				});
				acc = [...acc, ...defaultStepTags];
			}
			return acc;
		}, []);
		context.commit('setActiveStepTags', defaultStepsTags);
	},
};
const mutations = {
	setTagsToBeRestored: (state, tags) => {
		state.storedTags = { ...tags };
	},
	setActiveStep: (state, step) => {
		state.activeStep = step;
	},
	setTags: (state, payload) => {
		state.tags = payload['tags'];
		state.tagGroups = payload['tag-groups'];
	},
	setAssignments: (state, payload) => {
		state.assignments = { ...payload };
	},
	setActiveTags: (state, tags) => {
		state.activeTags = tags.reduce((acc, tag) => {
			let activeTagsInGroup = acc[tag['group-id']] || new Set();
			if (state.tagGroups[tag['group-id']].mode === 'XOR') activeTagsInGroup = new Set();
			activeTagsInGroup.add(tag.id);
			return { ...acc, [tag['group-id']]: activeTagsInGroup };
		}, clonedeep(state.activeTags));
	},
	clearActiveTags: (state) => {
		state.activeTags = {};
	},
	unsetActiveTags: (state, tags) => {
		state.activeTags = tags.reduce((acc, tag) => {
			if (acc[tag['group-id']] && acc[tag['group-id']].has(tag.id)) {
				let activeTagsInGroup = acc[tag['group-id']];
				activeTagsInGroup.delete(tag.id);
				return { ...acc, [tag['group-id']]: activeTagsInGroup };
			} else {
				return acc;
			}
		}, clonedeep(state.activeTags));
	},
	setActiveStepTags: (state, tags) => {
		state.activeStepTags = tags.reduce((acc, tag) => {
			const tagStep = tag['step'];
			const tagGroupId = tag['tag']['group-id'];
			const tagId = tag['tag']['id'];
			const groupsInStep = acc[tagStep] || {};
			let activeTagsInGroup = groupsInStep[tagGroupId] || new Set();

			if (state.tagGroups[tagGroupId].mode === 'XOR') activeTagsInGroup = new Set();

			activeTagsInGroup.add(tagId);
			return { ...acc, [tagStep]: { ...groupsInStep, [tagGroupId]: activeTagsInGroup } };
		}, clonedeep(state.activeStepTags));
	},

	clearActiveStepTags: (state, step) => {
		if (step) {
			state.activeStepTags[step] = {};
		} else {
			state.activeStepTags = {};
		}
	},

	unsetActiveStepTags: (state, tags) => {
		state.activeStepTags = tags.reduce((acc, tag) => {
			const tagStep = tag['step'];
			const tagGroupId = tag['tag']['group-id'];
			const tagId = tag['tag']['id'];
			acc[tagStep][tagGroupId].delete(tagId);
			return acc;
		}, clonedeep(state.activeStepTags));
	},
	setActiveAlternativeProductsTags: (state, { tags }) => {
		let updatedState = { ...state.activeAlternativeProductsTags };
		updatedState[state.activeStep] = tags.reduce((acc, tag) => {
			let activeTagsInGroup = acc[tag['group-id']] || new Set();
			if (state.tagGroups[tag['group-id']].mode === 'XOR') activeTagsInGroup = new Set();
			activeTagsInGroup.add(tag.id);
			return { ...acc, [tag['group-id']]: activeTagsInGroup };
		}, clonedeep(updatedState[state.activeStep]) || {});
		state.activeAlternativeProductsTags = updatedState;
	},
	clearActiveAlternativeProductsTags: (state) => {
		state.activeAlternativeProductsTags = {};
	},
	unsetActiveAlternativeProductsTags: (state, { tags }) => {
		let updatedState = { ...state.activeAlternativeProductsTags };
		updatedState[state.activeStep] = tags.reduce((acc, tag) => {
			if (acc[tag['group-id']] && acc[tag['group-id']].has(tag.id)) {
				let activeTagsInGroup = acc[tag['group-id']];
				activeTagsInGroup.delete(tag.id);
				return { ...acc, [tag['group-id']]: activeTagsInGroup };
			} else {
				return acc;
			}
		}, clonedeep(updatedState[state.activeStep]) || {});
		state.activeAlternativeProductsTags = updatedState;
	},
};

export default {
	namespaced: true,
	state,
	getters,
	actions,
	mutations,
};
